import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSalesService } from 'services/sales';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { SalesOptions } from 'types/sales';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { encrypt, decrypt } from 'utils/cryptoUtils';

// LocalStorage key for caching sales options
const SALES_OPTIONS_CACHE_KEY_BASE = 'abrasoft_sales_options_cache';

// Helper functions for encrypted localStorage caching
const getCachedSalesOptions = async (
  cacheKey: string,
): Promise<SalesOptions | null> => {
  try {
    const encrypted = localStorage.getItem(cacheKey);
    if (encrypted) {
      // Decrypt the cached data
      const decrypted = await decrypt(encrypted);
      return JSON.parse(decrypted) as SalesOptions;
    }
  } catch (error) {
    // If localStorage is unavailable, decryption fails, or data is corrupted, return null
    console.warn('Failed to read cached sales options:', error);
    // Clear corrupted cache
    try {
      localStorage.removeItem(cacheKey);
    } catch {
      // Ignore errors when clearing
    }
  }
  return null;
};

const setCachedSalesOptions = async (
  options: SalesOptions,
  cacheKey: string,
): Promise<void> => {
  try {
    // Encrypt the data before storing
    const jsonData = JSON.stringify(options);
    const encrypted = await encrypt(jsonData);
    localStorage.setItem(cacheKey, encrypted);
  } catch (error) {
    // If localStorage is unavailable or encryption fails, silently fail
    console.warn('Failed to cache sales options:', error);
  }
};

interface UseSalesOptionsReturn {
  salesOptions: SalesOptions | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * Custom hook to manage sales options with encrypted localStorage caching
 * - Loads from cache first (no loading UI if cache exists)
 * - Fetches fresh data in background
 * - Automatically updates cache with fresh data
 */
export function useSalesOptions(
  enabled: boolean = true,
): UseSalesOptionsReturn {
  const salesService = useSalesService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [salesOptions, setSalesOptions] = useState<SalesOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const salesOptionsCacheKey = useMemo(
    () => getCacheKeyWithCompany(SALES_OPTIONS_CACHE_KEY_BASE, activeCompanyId),
    [activeCompanyId],
  );

  const loadSalesOptions = useCallback(async () => {
    if (!enabled) {
      return;
    }

    // Don't fetch if company is not initialized yet
    if (!isCompanyInitialized || !activeCompanyId) {
      return;
    }

    // Check cache first
    const cachedOptions = await getCachedSalesOptions(salesOptionsCacheKey);
    const hasCache = cachedOptions !== null;

    // If cache exists, use it immediately (no loading UI)
    if (cachedOptions) {
      setSalesOptions(cachedOptions);
      setLoading(false);
      setError(null);
    }

    // Fetch fresh data in background (or if no cache, show loading)
    try {
      const options = await salesService.getSalesOptions();
      // Update state with fresh data
      setSalesOptions(options);
      setError(null);
      // Update cache with fresh data (encrypted)
      void setCachedSalesOptions(options, salesOptionsCacheKey).catch(
        (cacheError) => {
          // Silently fail if caching fails - fresh data is already in state
          console.warn('Failed to cache sales options:', cacheError);
        },
      );
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load sales options. Please try again.';
      // Only set error if we don't have cached data
      if (!hasCache) {
        setError(errorMessage);
      }
      // If we have cache, silently fail (user already sees cached data)
    } finally {
      // Only set loading to false if we didn't have cache
      if (!hasCache) {
        setLoading(false);
      }
    }
  }, [
    salesService,
    enabled,
    salesOptionsCacheKey,
    isCompanyInitialized,
    activeCompanyId,
  ]);

  // Reload function to manually refresh data
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const options = await salesService.getSalesOptions();
      setSalesOptions(options);
      setError(null);
      // Update cache with fresh data (encrypted)
      await setCachedSalesOptions(options, salesOptionsCacheKey);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to reload sales options. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [salesService, salesOptionsCacheKey]);

  // Reset when company changes
  useEffect(() => {
    setLoading(true);
    setSalesOptions(null);
    setError(null);
  }, [activeCompanyId]);

  // Load on mount and when enabled or company changes
  useEffect(() => {
    if (isCompanyInitialized) {
      void loadSalesOptions();
    }
  }, [loadSalesOptions, isCompanyInitialized]);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      salesOptions,
      loading,
      error,
      reload,
    }),
    [salesOptions, loading, error, reload],
  );
}
